import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FloorPlanData } from '@shared/types/floorPlans';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { getFloorPlansForRestaurantId } from 'restaurantAdmin/floorPlans/apiHelpers';
import { useRestaurant } from '../context/useRestaurant';

export interface UseAdminFloorPlansOptions {
  date?: Date;
}

interface UseAdminFloorPlanReturn {
  floorPlans: FloorPlanData[];
  isLoading: boolean;
  isReadyForDisplay: boolean;
  fetchFloorPlans: () => void;
  selectedFloorPlan: FloorPlanData;
  setSelectedFloorPlanId: Dispatch<SetStateAction<string>>;
}

const NULL_FLOOR_PLAN = {
  id: '',
  backgroundSrc: undefined,
  floorPlanTables: [],
  name: '',
};

export const useAdminFloorPlans = (
  options?: UseAdminFloorPlansOptions,
): UseAdminFloorPlanReturn => {
  const { id: restaurantId } = useRestaurant();
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<string>('');

  const {
    data: floorPlans = [],
    isPending: isLoading,
    isRefetching,
    refetch: fetchFloorPlans,
  } = useQuery({
    queryKey: ['floorPlans', restaurantId],
    queryFn: async () => {
      try {
        return await getFloorPlansForRestaurantId(
          restaurantId,
          options?.date && toISODateFormat(options.date),
        );
      } catch {
        return [NULL_FLOOR_PLAN];
      }
    },
  });

  useEffect(() => {
    if (!isLoading && floorPlans.length > 0) {
      const selectedFloorPlan = floorPlans.find(
        (fp) => fp.id === selectedFloorPlanId,
      );

      if (!selectedFloorPlan) {
        setSelectedFloorPlanId(floorPlans[0].id);
      }
    }
  }, [floorPlans, isLoading, isRefetching]);

  useEffect(() => {
    if (!isRefetching && !isLoading) {
      fetchFloorPlans();
    }
  }, [options?.date]);

  const selectedFloorPlan =
    floorPlans.find((fp: FloorPlanData) => fp.id === selectedFloorPlanId) ||
    NULL_FLOOR_PLAN;

  return {
    floorPlans,
    isLoading: isRefetching || isLoading,
    isReadyForDisplay:
      !isLoading && selectedFloorPlan.id !== NULL_FLOOR_PLAN.id,
    fetchFloorPlans,
    selectedFloorPlan,
    setSelectedFloorPlanId,
  };
};
